import Alert from "@pathwright/ui/src/components/alert/Alert"
import Button from "@pathwright/ui/src/components/button/Button"
import SubmitButton from "@pathwright/ui/src/components/button/SubmitButton"
import FullscreenConfirm from "@pathwright/ui/src/components/confirm/FullscreenConfirm"
import ToggleInput from "@pathwright/ui/src/components/form/form-toggle/ToggleInput"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import View from "@pathwright/ui/src/components/ui/View"
import { useMutationWithDefaults } from "@pathwright/utils/src/graphql/mutation"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { Form, Formik } from "formik"
import { useEffect, useRef } from "react"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import {
  FeatureAction,
  FeatureContextInput,
  FeatureKey,
  FeaturePermission,
  UpdateContextualFeaturePermissionMutationVariables,
  useContextualFeaturePermissionQuery,
  useUpdateContextualFeaturePermissionMutation
} from "@pathwright/graphql-client/src/generated"
import { useIntercomContext } from "../../intercom/IntercomProvider"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { useFeatureUpdatePermission } from "../permissions"
import { featurePermissionsQueryPropTypes } from "../propTypes"
import { getFeatureInfo, getFeaturePermissionInfo } from "../utils"
import FeatureVisibilitySelect from "./FeatureVisibilitySelect"

const Confirm = ({
  children,
  featureKey,
  permission,
  ...confirmProps
}: {
  children: any
  featureKey: FeatureKey
  permission: FeaturePermission
} & React.ComponentProps<typeof FullscreenConfirm> & {}) => {
  const { t } = useTranslate()
  const { school } = usePathwrightContext()

  return (
    <FullscreenConfirm
      icon="caution-triangle"
      heading={t("Update Visibilty for All Cohorts?")}
      body={t(
        "The {{ feature }} will be visible to {{ audience }} in all cohorts in {{ school }}. (Cohorts with customized visibility settings won't be be updated.)",
        {
          feature: getFeatureInfo(featureKey, t).title,
          audience: getFeaturePermissionInfo(featureKey, permission, t).title,
          school: school.name
        }
      )}
      confirmPrompt={t("Yes, Update Visibility")}
      cancelPrompt={t("Nevermind, go back")}
      stack
      {...confirmProps}
    >
      {children}
    </FullscreenConfirm>
  )
}

export const useFeaturePermission = <T extends FeatureAction>({
  featureKey,
  featureAction,
  context
}: {
  featureKey: FeatureKey
  featureAction: T
  context: FeatureContextInput | null
}) => {
  const { school } = usePathwrightContext()
  const { usingPathwrightIntercom } = useIntercomContext()

  // Consumer of mutation provides the permissionInContext variable.
  const variables = {
    featureKey,
    featureAction,
    context
  } satisfies Omit<
    UpdateContextualFeaturePermissionMutationVariables,
    "permissionInContext"
  >

  // ugly way to retain the original permission_in_context for event tracking
  // likely in ApollClient 3.0^ we could simply check the permission.query.previousData
  const originalPermissionRef = useRef<FeaturePermission | null>(null)

  const query = useContextualFeaturePermissionQuery({
    variables
  })

  // Temporary negative ID for optimistic item.
  const randomId = -Math.floor(Math.random() * 1000000)

  const existingId = query.data?.contextualFeaturePermission?.id
  const cacheId = existingId ? existingId : randomId

  const permission = {
    key: featureAction as T,
    query,
    mutation: useMutationWithDefaults(
      useUpdateContextualFeaturePermissionMutation,
      {
        variables,
        optimisticResponse: (variables) => ({
          __typename: "Mutations" as const,
          updateContextualFeaturePermission: {
            __typename: "ContextualFeaturePermission" as const,
            id: cacheId,
            feature_key: variables.featureKey,
            feature_action: variables.featureAction,
            permission_in_context: variables.permissionInContext,
            school_id: school.id,
            resource_id: variables.context?.resource_id ?? null,
            cohort_id: variables.context?.cohort_id ?? null,
            user_id: variables.context?.user_id ?? null
          }
        }),
        onCompleted: (result) => {
          // Refetch only if the CFP is being created since the query doesn't have this CFP yet. Otherwise,
          // the optimistic response will propagate to that query data.
          if (!existingId) {
            query.refetch()
          }
          // tracking an Intercom event for when the user changes a cfp's permission_in_context
          // though only for when Pathwright Intercom is enabled
          if (usingPathwrightIntercom && window.Intercom) {
            const cfp = result.updateContextualFeaturePermission
            if (cfp) {
              const eventData = Object.entries({
                feature: cfp.feature_key,
                action: cfp.feature_action,
                school: cfp.school_id,
                resource: cfp.resource_id,
                cohort: cfp.cohort_id,
                from: originalPermissionRef.current,
                to: cfp.permission_in_context
              }).reduce(
                // remove any null values (potentially resource and cohort)
                (data, [key, value]) =>
                  value ? { ...data, [key]: value } : data,
                {}
              )

              window.Intercom(
                "trackEvent",
                "changed-feature-visibility",
                eventData
              )
            }
          }
        }
      }
    ),
    canUpdate: useFeatureUpdatePermission(variables).hasPermission
  }

  useEffect(() => {
    if (permission.query.data) {
      // ugly again, having to delay slightly before updating originalPermissionRef
      // as the query data changes as a result of mutation BEFORE the onCompleted callback runs
      setTimeout(() => {
        originalPermissionRef.current =
          permission.query.data?.contextualFeaturePermission
            ?.permission_in_context || null
      }, 50)
    }
  }, [permission.query.data])

  return permission
}

// extending permissions for form
const useFeaturePermissions = ({
  featureKey,
  context
}: {
  featureKey: FeatureKey
  context: FeatureContextInput | null
}) => {
  const permissions = {
    [FeatureAction.View]: {
      ...useFeaturePermission({
        featureKey,
        featureAction: FeatureAction.View,
        context
      }),
      getInitialValue: function () {
        if (!this.query.data) return undefined
        return this.query.data.contextualFeaturePermission
          ?.permission_in_context!
      },
      getFormValue: function (values: FormValues) {
        return values[this.key]
      }
    },
    [FeatureAction.UpdateVisibility]: {
      ...useFeaturePermission({
        featureKey,
        featureAction: FeatureAction.UpdateVisibility,
        context
      }),
      getInitialValue: function () {
        if (!this.query.data) return undefined
        return (
          this.query.data.contextualFeaturePermission?.permission_in_context ===
          FeaturePermission.StaffOnly
        )
      },
      getFormValue: function (values: FormValues) {
        return values[this.key]
          ? FeaturePermission.StaffOnly
          : FeaturePermission.AdminOnly
      }
    }
  }

  return permissions
}

const FormSchema = z.object({
  [FeatureAction.View]: z.nativeEnum(FeaturePermission),
  [FeatureAction.UpdateVisibility]: z.boolean()
})

type FormValues = z.infer<typeof FormSchema>

const validator = toFormikValidate(FormSchema)

const FeaturePermissionsForm = ({
  featureKey,
  context
}: {
  featureKey: FeatureKey
  context: FeatureContextInput | null
}) => {
  const formikRef = useRef(null)
  const { t } = useTranslate()
  const { title: featureTitle } = getFeatureInfo(featureKey, t)
  const permissions = useFeaturePermissions({ featureKey, context })

  const { loading, error } = Object.values(permissions).reduce(
    (state, { query }) => ({
      loading: state.loading || query.loading,
      error: state.error || query.error
    }),
    {} as { loading: boolean; error?: Error }
  )

  if (loading) {
    return <LoadingCircle center />
  }

  if (error) {
    return <Alert error={error} />
  }

  // NOTE: always performing an update mutation even though at times
  // there may not exist a CFP–the backend automatically creates if no CFP exists
  // even when the request is to update an existing CFP
  async function handleSubmit(values: FormValues) {
    await Promise.all(
      Object.values(permissions)
        // filter out unchanged permissions
        .filter((config) => values[config.key] !== initialValues[config.key])
        // filter out permissions which user cannot update
        .filter((config) => config.canUpdate)
        .map((config) =>
          config.mutation[0]({
            variables: {
              permissionInContext: config.getFormValue(values) || null
            }
          })
        )
    )
  }

  const initialValues: FormValues = {
    [FeatureAction.View]: permissions[FeatureAction.View].getInitialValue()!,
    [FeatureAction.UpdateVisibility]:
      permissions[FeatureAction.UpdateVisibility].getInitialValue()!
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      // enableReinitialize allows the form to reset to new state after submission
      enableReinitialize
      onSubmit={handleSubmit}
      validate={validator}
    >
      {(form) => {
        const { values, dirty, setFieldValue, resetForm } = form

        return (
          <Form style={{ minWidth: "100%" }}>
            <div /* wrapper for View separate prop to work */>
              <View separate paddingTop paddingBottom>
                <FeatureVisibilitySelect
                  labelWidth={200}
                  label={t("Make visible to")}
                  helperText={t(
                    `Sets the visibility of the {{feature}} for all cohorts.`,
                    {
                      feature: featureTitle
                    }
                  )}
                  alignRight
                  value={values[FeatureAction.View]}
                  onChange={(value: FeaturePermission) =>
                    setFieldValue(FeatureAction.View, value, false)
                  }
                  isDisabled={!permissions[FeatureAction.View].canUpdate}
                  featureKey={featureKey}
                />
              </View>
              {!context && (
                <View separate paddingTop paddingBottom>
                  <ToggleInput
                    labelWidth={260}
                    label={t("Enable staff to customize visibility")}
                    helperText={t(
                      "Enable editors, moderators, and teachers to customize the {{ feature }} visibility for cohorts they have access to.",
                      {
                        feature: featureTitle
                      }
                    )}
                    value={values[FeatureAction.UpdateVisibility]}
                    onChange={(value: boolean) =>
                      setFieldValue(
                        FeatureAction.UpdateVisibility,
                        value,
                        false
                      )
                    }
                    disabled={
                      !permissions[FeatureAction.UpdateVisibility].canUpdate
                    }
                    alignRight
                  />
                </View>
              )}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {context ? (
                <SubmitButton styleType="primary" disabled={!dirty} brand>
                  {t("Update Visibility")}
                </SubmitButton>
              ) : (
                <Confirm
                  onConfirm={() => handleSubmit(form.values)}
                  onCancel={() => resetForm()}
                  featureKey={featureKey}
                  permission={permissions[FeatureAction.View].getFormValue(
                    values
                  )}
                >
                  {({ confirm }: any) => (
                    <Button
                      styleType="primary"
                      onClick={confirm}
                      disabled={!dirty}
                      brand
                    >
                      {t("Update Visibility")}
                    </Button>
                  )}
                </Confirm>
              )}
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

FeaturePermissionsForm.displayName = "FeaturePermissionsForm"

FeaturePermissionsForm.propTypes = featurePermissionsQueryPropTypes

export default FeaturePermissionsForm
